import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Link } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { count, when } from '../../ui/format.js'
import { EmptyCard } from '../../ui/PagedScreen.js'
import { TableSkeleton } from '../../ui/Pager.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Table, Td, TdNum, Tr } from '../../ui/Table.js'
import { useMay } from '../../ui/yourRole.js'

import { CampaignCalendar } from './CampaignCalendar.js'
import { audienceOf, CAMPAIGN_STATUS, campaignHome } from './campaignLabels.js'
import { useCampaigns } from './useCampaigns.js'

/**
 * Every campaign, and the one button that sends one.
 *
 * A draft links to the review screen. Anything else shows what happened to it
 * and no control at all — a campaign is queued once, and offering one again
 * would imply a second send is a thing that could happen.
 *
 * Nothing is sent from this screen. It used to be, and one press from a row
 * beside a delete button is too little between the thought and several thousand
 * emails; the review is where that decision is now made, with the checks in
 * front of it.
 *
 * The audience column is a number for a queued campaign and a name for a draft,
 * and that difference is the point: a draft has no audience yet. A segment is a
 * question, answered at the moment of sending, so anything shown before then
 * would be a different number from the one that goes out.
 */
export function CampaignsPage({ client }: { readonly client: ApiClient }) {
  const { state, notice, remove, retry } = useCampaigns({ client })
  /**
   * Two views of one list, and one fetch behind both.
   *
   * The table answers "what happened to this campaign"; the calendar answers
   * "how often are we mailing these people", which is the question that decides
   * whether a list gets tired of you. A second request for the second view
   * would be a second thing to keep in step for no gain — `list` already
   * returns every campaign.
   */
  const [view, setView] = useState<'table' | 'calendar'>('table')

  /*
   * A viewer reads this list and starts nothing from it.
   *
   * Writing a campaign and sending one are separate permissions, so they are
   * asked separately: a role could gain one without the other, and a single
   * flag here would be the place that quietly decided otherwise.
   */
  const mayWrite = useMay('campaigns', 'write')
  const maySend = useMay('campaigns', 'send')

  return (
    <>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Campaigns</h1>
          <p className="mt-1 text-[13px] text-t2">
            A campaign goes out once. Its audience is worked out when you send it, not when the
            segment was saved.
          </p>
        </div>
        {!mayWrite ? null : (
          <Link
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast"
            to="/campaigns/new"
          >
            New campaign
          </Link>
        )}
      </header>

      {notice === undefined ? null : <Alert tone={notice.tone}>{notice.text}</Alert>}

      <div className="mb-4 flex gap-2">
        <ViewTab chosen={view === 'table'} onChoose={() => setView('table')}>
          Table
        </ViewTab>
        <ViewTab chosen={view === 'calendar'} onChoose={() => setView('calendar')}>
          Calendar
        </ViewTab>
      </div>

      <Async state={state} skeleton={<TableSkeleton />} onRetry={() => void retry()}>
        {(result) =>
          result.campaigns.length === 0 ? (
            <EmptyCard heading="No campaigns yet">
              A campaign is one message to one audience. Write it, choose who it goes to, and send
              it — nothing leaves until you do.
            </EmptyCard>
          ) : view === 'calendar' ? (
            <CampaignCalendar campaigns={result.campaigns} />
          ) : (
            <Table head={['Name', 'Audience', 'Status', 'Queued', 'Created', '']} minWidth={840}>
              {result.campaigns.map((campaign) => (
                <Row
                  key={campaign.campaignId}
                  campaign={campaign}
                  mayWrite={mayWrite}
                  maySend={maySend}
                  onRemove={remove}
                />
              ))}
            </Table>
          )
        }
      </Async>
    </>
  )
}

/**
 * A tab, as a button rather than a link.
 *
 * Which view somebody is looking at is not a different place: the data and the
 * scroll position are the same, and a URL for it would be a second thing to
 * keep in step with no question it answers.
 */
function ViewTab({
  chosen,
  onChoose,
  children,
}: {
  readonly chosen: boolean
  readonly onChoose: () => void
  readonly children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onChoose}
      className={`rounded-pill border px-4 py-1.5 font-semibold text-[13px] ${
        chosen ? 'border-brand bg-brand-soft text-brand' : 'border-line bg-surface text-t2'
      }`}
    >
      {children}
    </button>
  )
}

function Row({
  campaign,
  mayWrite,
  maySend,
  onRemove,
}: {
  readonly campaign: CampaignDto
  readonly mayWrite: boolean
  readonly maySend: boolean
  readonly onRemove: (campaignId: string) => Promise<void>
}) {
  const status = CAMPAIGN_STATUS[campaign.status]

  return (
    <Tr>
      <Td strong>
        {/* A draft opens where it can be written; anything else opens its report. */}
        <Link className="text-brand" to={campaignHome(campaign)}>
          {campaign.name}
        </Link>
      </Td>
      <Td>{audienceOf(campaign)}</Td>
      <Td>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </Td>
      {/* An em dash rather than 0. A draft has no audience yet, and a zero here
          would read as one that reaches nobody. */}
      <TdNum>{campaign.queuedCount === null ? '—' : count(campaign.queuedCount)}</TdNum>
      <Td>{when(campaign.createdAt)}</Td>
      <Td>
        <span className="flex items-center gap-3">
          {/*
            Only for a campaign already sent. A draft's own name opens the
            composer, and a second link to the same screen in the same row is
            the duplication this product is a rebuild to avoid.
          */}
          {campaign.status === 'draft' ? null : (
            <Link
              className="font-medium text-[13px] text-t3 hover:text-t1"
              to={`/campaigns/${campaign.campaignId}/edit`}
            >
              View message
            </Link>
          )}
          {/*
            A link to the review, never a send. Sending used to happen here, one
            press from a row beside a delete button, with nothing between the
            thought and several thousand emails.
          */}
          {campaign.status === 'draft' && maySend ? (
            <Link
              className="rounded-pill border border-brand px-3.5 py-1.5 font-semibold text-[13px] text-brand hover:bg-brand-soft"
              to={`/campaigns/${campaign.campaignId}/send`}
            >
              Review and send
            </Link>
          ) : null}
          {/*
            Offered on drafts only. A sent campaign's row is what the send log
            and the queue hang off, so there is nothing to delete without
            erasing the record of what went out.
          */}
          {campaign.status === 'draft' && mayWrite ? (
            <button
              type="button"
              onClick={() => void onRemove(campaign.campaignId)}
              className="font-medium text-[13px] text-t3 hover:text-danger"
            >
              Delete
            </button>
          ) : null}
        </span>
      </Td>
    </Tr>
  )
}
